import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import Navbar from '../components/navbar';
import '../css/chapter.css';
import { Helmet } from '@dr.pogodin/react-helmet';

function useCountdown(targetISO) {
    const [now, setNow] = useState(() => new Date());
    useEffect(() => {
        const t = setInterval(() => setNow(new Date()), 1000);
        return () => clearInterval(t);
    }, []);
    return useMemo(() => {
        if (!targetISO) return null;
        const target = new Date(targetISO);
        if (isNaN(target.getTime())) return null;
        const ms = target.getTime() - now.getTime();
        const diff = Math.abs(ms);
        const days = Math.floor(diff / (24 * 3600 * 1000));
        const hours = Math.floor((diff % (24 * 3600 * 1000)) / (3600 * 1000));
        const minutes = Math.floor((diff % (3600 * 1000)) / (60 * 1000));
        const seconds = Math.floor((diff % (60 * 1000)) / 1000);
        return { ms, days, hours, minutes, seconds };
    }, [targetISO, now]);
}

function parseDateParts(dateStr) {
    const s = String(dateStr || "").trim();
    if (!s) return null;

    // YYYY-MM-DD
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
        const [y, m, d] = s.split("-").map(Number);
        return { y, m, d };
    }

    // MM/DD/YYYY
    if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(s)) {
        const [m, d, y] = s.split("/").map(Number);
        return { y, m, d };
    }

    // fallback
    const dt = new Date(s);
    if (!isNaN(dt.getTime())) {
        return { y: dt.getFullYear(), m: dt.getMonth() + 1, d: dt.getDate() };
    }
    return null;
}

function parseTimeParts(timeStr) {
    const s = String(timeStr || "").trim();
    if (!s) return { hh: 0, mm: 0, ss: 0 };

    // 24h: HH:MM or HH:MM:SS
    if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(s)) {
        const [hh, mm, ss = "0"] = s.split(":");
        return { hh: Number(hh) || 0, mm: Number(mm) || 0, ss: Number(ss) || 0 };
    }

    // 12h: h:mm AM/PM
    const m = s.match(/^(\d{1,2}):(\d{2})\s*([AaPp][Mm])$/);
    if (m) {
        let hh = Number(m[1]) || 0;
        const mm = Number(m[2]) || 0;
        const ap = m[3].toUpperCase();
        if (ap === "PM" && hh < 12) hh += 12;
        if (ap === "AM" && hh === 12) hh = 0;
        return { hh, mm, ss: 0 };
    }

    return { hh: 0, mm: 0, ss: 0 };
}

function toLocalDate(dateStr, timeStr) {
    const dp = parseDateParts(dateStr);
    if (!dp) return new Date(NaN);
    const tp = parseTimeParts(timeStr);
    return new Date(dp.y, (dp.m || 1) - 1, dp.d || 1, tp.hh, tp.mm, tp.ss);
}

function fmtDate(dateStr, timeStr) {
    try {
        const d = toLocalDate(dateStr, timeStr);
        if (isNaN(d.getTime())) return `${dateStr}${timeStr ? ` ${timeStr}` : ""}`;

        const hasTime = Boolean(String(timeStr || "").trim());
        return d.toLocaleString(undefined, {
            weekday: "short",
            month: "short",
            day: "numeric",
            year: "numeric",
            hour: hasTime ? "numeric" : undefined,
            minute: hasTime ? "2-digit" : undefined,
        });
    } catch {
        return `${dateStr}${timeStr ? ` ${timeStr}` : ""}`;
    }
}

function formatICSDate(dt) {
    if (!(dt instanceof Date) || isNaN(dt.getTime())) return "";
    return dt.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
}

function buildCalendarLinks(event, chapterTitle, chapterUrl) {
    const title = event.title || 'Event';
    const description = event.description || '';
    const location = event.location || '';
    const baseName = chapterTitle ? `${title} — ${chapterTitle}` : title;

    const details = chapterUrl
        ? `${description}\n\nMore details: ${chapterUrl}`
        : description;

    const start = toLocalDate(event.date, event.time);
    const end = new Date(start.getTime() + 90 * 60 * 1000); // default 90-min event

    const startStr = formatICSDate(start);
    const endStr = formatICSDate(end);

    // Google Calendar link
    const gcalParams = new URLSearchParams({
        action: 'TEMPLATE',
        text: title,
        dates: `${startStr}/${endStr}`,
        details,      // ⬅ use the enriched description
        location,
    });
    const google = `https://www.google.com/calendar/render?${gcalParams.toString()}`;

    // Simple ICS file (data URI)
    const icsLines = [
        'BEGIN:VCALENDAR',
        'VERSION:2.0',
        'PRODID:-//soleministries.org//EN',
        'BEGIN:VEVENT',
        `UID:${startStr}-${encodeURIComponent(baseName)}`,
        `DTSTAMP:${formatICSDate(new Date())}`,
        `DTSTART:${startStr}`,
        `DTEND:${endStr}`,
        `SUMMARY:${title}`,
        details ? `DESCRIPTION:${details.replace(/\r?\n/g, '\\n')}` : '',
        location ? `LOCATION:${location}` : '',
        'END:VEVENT',
        'END:VCALENDAR',
    ].filter(Boolean);

    const icsContent = icsLines.join('\r\n');
    const ics = `data:text/calendar;charset=utf8,${encodeURIComponent(icsContent)}`;

    return { google, ics };
}
export default function ChapterPage() {
    const { schoolId } = useParams();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [school, setSchool] = useState(null);
    const [events, setEvents] = useState([]);
    const [activeSemester, setActiveSemester] = useState(null);


    useEffect(() => {
        let cancel = false;
        async function run() {
            try {
                setLoading(true);
                const ref = doc(db, 'schools', String(schoolId || '').trim());
                const snap = await getDoc(ref);
                if (!snap.exists()) throw new Error('Chapter not found');
                if (cancel) return;
                const data = snap.data() || {};
                const rawSemesters = Array.isArray(data.semesters)
                    ? data.semesters
                    : (Array.isArray(data.talkSemesters) ? data.talkSemesters
                        : (Array.isArray(data.eventSemesters) ? data.eventSemesters : []));

                const normalizeDateOnly = (v) => {
                    const dp = parseDateParts(v);
                    if (!dp) return null;
                    return new Date(dp.y, (dp.m || 1) - 1, dp.d || 1, 0, 0, 0);
                };

                const today0 = new Date();
                today0.setHours(0, 0, 0, 0);

                const semesters = (rawSemesters || []).map((s) => {
                    const name = s?.name || s?.semesterName || s?.title || "";
                    const publicFrom = s?.publicFrom || s?.publicStart || s?.windowStart || s?.start || null;
                    const publicTo = s?.publicTo || s?.publicEnd || s?.windowEnd || s?.end || null;
                    return {
                        ...s,
                        name,
                        publicFrom,
                        publicTo,
                        _from: normalizeDateOnly(publicFrom),
                        _to: normalizeDateOnly(publicTo),
                    };
                });

                const isInWindow = (sem) => {
                    if (!sem?._from || !sem?._to) return false;
                    return sem._from <= today0 && today0 <= sem._to;
                };

                // choose current public semester, else closest upcoming, else most recent past
                // Only show a semester on the public chapter page if we're currently inside its public window.
                const chosen = semesters.find(isInWindow) || null;
                setActiveSemester(chosen);

                setActiveSemester(chosen);

                const evsAll = Array.isArray(data.events) ? data.events : [];
                let evsPublic = [];

                if (chosen) {
                    if (Array.isArray(chosen.events)) {
                        evsPublic = chosen.events;
                    } else if (chosen._from && chosen._to) {
                        const start = chosen._from;
                        const end = new Date(chosen._to.getFullYear(), chosen._to.getMonth(), chosen._to.getDate(), 23, 59, 59);
                        evsPublic = evsAll.filter((e) => {
                            const dt = toLocalDate(e.date, e.time || "00:00");
                            if (isNaN(dt.getTime())) return false;
                            return dt >= start && dt <= end;
                        });
                    }
                }

                // Sort and set
                const toDate = (e) => toLocalDate(e.date, e.time || "00:00");
                setEvents([...evsPublic].sort((a, b) => toDate(a) - toDate(b)));
                setSchool({ id: schoolId, ...data });
            } catch (e) {
                console.error(e);
                setError('Could not load chapter.');
            } finally {
                setLoading(false);
            }
        }
        if (schoolId) run();
        return () => { cancel = true; };
    }, [schoolId]);

    // Determine next event ISO _before_ any early returns so hooks order is stable
    const nextEventISO = useMemo(() => {
        if (!Array.isArray(events) || events.length === 0) return null;
        const now = new Date();
        for (const e of events) {
            const dt = toLocalDate(e.date, e.time || "00:00");
            if (!isNaN(dt.getTime()) && dt >= now) {
                return dt.toISOString();
            }
        }
        return null;
    }, [events]);

    const cd = useCountdown(nextEventISO);

    // Safe guards while loading
    if (loading) return <div className="chapter-public"><div className="chapter-public__box">Loading…</div></div>;
    if (error) return <div className="chapter-public"><div className="chapter-public__box">{error}</div></div>;
    if (!school) return <div className="chapter-public"><div className="chapter-public__box">Chapter not found.</div></div>;

    const title = school.displayName || school.name || school.schoolName || `SOLE at ${schoolId?.toUpperCase?.() || ''}`;
    const isActiveChapter = (school.active ?? school.isActive ?? school.chapterActive ?? school.isChapterActive);
    const chapterActive = (typeof isActiveChapter === "boolean") ? isActiveChapter : true;
    const semesterName = activeSemester?.name || "";

    const formatWindowRange = (fromDate, toDate) => {
        if (!(fromDate instanceof Date) || isNaN(fromDate.getTime())) return "";
        if (!(toDate instanceof Date) || isNaN(toDate.getTime())) return "";

        // Example: "Dec 28, 2025 – May 10, 2026"
        const opts = { month: "short", day: "numeric", year: "numeric" };
        const fromStr = fromDate.toLocaleDateString(undefined, opts);
        const toStr = toDate.toLocaleDateString(undefined, opts);
        return `${fromStr} – ${toStr}`;
    };

    const windowLabel = (activeSemester?._from && activeSemester?._to)
        ? formatWindowRange(activeSemester._from, activeSemester._to)
        : "";

    const semesterPillLabel = activeSemester
        ? (semesterName && windowLabel ? `${semesterName} ` : (semesterName))
        : "";
    const chapterUrl = `https://soleministries.org/schools/${schoolId}`;
    const announcements = school.announcements || '';
    const bannerUrl = school.bannerUrl || school.heroImageUrl || school.bannerImage || '';
    const ctaEmail = school.ctaEmail || school.emailListUrl || '';
    const ctaGroupMe = school.ctaGroupMe || school.groupMeUrl || '';
    const ctaInstagram = school.ctaInstagram || school.instagramUrl || '';
    const ctaWebsite = school.chapterWebsite || school.websiteUrl || '';

    // Split events into next, upcoming, past
    const now = new Date();
    const upcomingAll = [];
    const pastAll = [];
    for (const e of events) {
        const dt = new Date(`${e.date || ''}T${e.time || '00:00'}`);
        if (!isNaN(dt) && dt >= now) upcomingAll.push(e); else pastAll.push(e);
    }
    const nextEvent = upcomingAll[0] || null;
    const otherUpcoming = upcomingAll.slice(1);
    const past = pastAll.reverse();

    const nextEventCalLinks = nextEvent
        ? buildCalendarLinks(nextEvent, title, chapterUrl)
        : null;

    return (
        <>
            <Helmet>
                <title>SOLE — {title} Chapter</title>
            </Helmet>
            <Navbar />
            <div className="chapter-public">
                <div className='chapter-content'>

                    {/* Hero */}
                    <div className="chapter-public__hero" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                        <h1 style={{ margin: 0 }}>{title}</h1>

                        {(!!semesterPillLabel || !chapterActive) && (
                            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>

                                {!chapterActive && (
                                    <span style={{
                                        fontSize: 12,
                                        fontWeight: 700,
                                        padding: "6px 10px",
                                        borderRadius: 999,
                                        background: "#f1f3f5",
                                        border: "1px solid #e7ebf3",
                                        color: "#495057",
                                    }}>
                                        Inactive
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                    {!chapterActive && (
                        <div className="chapter-public__box">
                            <h2 style={{ margin: 0 }}>This chapter isn’t public yet</h2>
                            <p style={{ margin: '6px 0 0', color: '#6c757d' }}>
                                This chapter is currently marked as inactive. Public events and announcements will appear once it is activated.
                            </p>
                        </div>
                    )}
                    {/* Announcements */}
                    {!!announcements && (
                        <div className="chapter-public__box">
                            <h2 style={{ margin: 0 }}>Announcements</h2>
                            <p style={{ margin: '6px 0 0', whiteSpace: 'pre-wrap' }}>{announcements}</p>
                        </div>
                    )}

                    {/* Upcoming */}
                    <div className="chapter-public__box">
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                            <h2 style={{ margin: 0 }}>Upcoming Events</h2>
                            <small>
                                {events.length
                                    ? `${events.length} events${semesterName ? ` • ${semesterName}` : ""}`
                                    : (semesterName ? `More events coming soon • ${semesterName}` : "More events coming soon")}
                            </small>
                        </div>

                        {!nextEvent && (
                            <p style={{ marginTop: 8 }}>More event details coming soon.</p>
                        )}

                        {nextEvent && (
                            <div className="chapter-public__feature">
                                <div>
                                    <h3 style={{ margin: 0 }}>{nextEvent.title || 'Event'}</h3>
                                    <div style={{ marginTop: 6, color: '#444' }}>{fmtDate(nextEvent.date, nextEvent.time)}</div>
                                    {nextEvent.location && (
                                        <div style={{ marginTop: 4 }}><strong>Location:</strong> {nextEvent.location}</div>
                                    )}
                                    {nextEvent.description && (
                                        <p style={{ marginTop: 10 }}>{nextEvent.description}</p>
                                    )}
                                </div>
                                {cd && (
                                    <div className="chapter-public__countdown">
                                        <div style={{ fontWeight: 700, marginBottom: 4 }}>{cd.ms >= 0 ? 'Starts in' : 'Started'}:</div>
                                        <div style={{ fontSize: 22, fontWeight: 800 }}>
                                            {cd.days}d {String(cd.hours).padStart(2, '0')}h {String(cd.minutes).padStart(2, '0')}m {String(cd.seconds).padStart(2, '0')}s
                                        </div>
                                    </div>
                                )}
                                {nextEventCalLinks && (
                                    <div className="chapter-public__cal-buttons">
                                        <a href={nextEventCalLinks.google} target="_blank" rel="noreferrer">
                                            <button className="secondary">Add to Google Calendar</button>
                                        </a>
                                        <a
                                            href={nextEventCalLinks.ics}
                                            download={`${(nextEvent.title || 'event').replace(/\s+/g, '-').toLowerCase()}.ics`}
                                        >
                                            <button className="secondary">Download .ics</button>
                                        </a>
                                    </div>
                                )}
                            </div>
                        )}

                        {otherUpcoming?.length > 0 && (
                            <div className="chapter-public__list chapter-public__rows">
                                {otherUpcoming.map((e, i) => (
                                    <div key={i} className="chapter-public__row">
                                        <div className="chapter-public__title">{e.title || 'Event'}</div>
                                        <div className="chapter-public__date">{fmtDate(e.date, e.time)}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>


                    {/* Past Events */}
                    {past?.length > 0 && (
                        <div className="chapter-public__box">
                            <h2 style={{ margin: 0 }}>Past Events</h2>
                            <div className="chapter-public__list chapter-public__rows">
                                {past.map((e, i) => (
                                    <div key={i} className="chapter-public__row chapter-public__row--muted">
                                        <div className="chapter-public__title">{e.title || 'Event'}</div>
                                        <div className="chapter-public__date">{fmtDate(e.date, e.time)}</div>
                                        <div className="chapter-public__actions">
                                            {(() => {
                                                const notesUrl = e.talkNotesLink || e.talkLink || '';
                                                const recordingUrl = e.talkRecordingLink || '';

                                                if (!notesUrl && !recordingUrl) {
                                                    return <span style={{ color: '#999' }}>—</span>;
                                                }

                                                return (
                                                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                                                        {notesUrl ? (
                                                            <a href={notesUrl} target="_blank" rel="noreferrer">View Notes</a>
                                                        ) : null}
                                                        {recordingUrl ? (
                                                            <a href={recordingUrl} target="_blank" rel="noreferrer">Listen to Talk</a>
                                                        ) : null}
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    {/* Get Involved / CTAs */}
                    {(ctaEmail || ctaGroupMe || ctaInstagram || ctaWebsite) && (
                        <div className="chapter-public__box">
                            <h2 style={{ margin: 0 }}>Get Involved</h2>
                            <p style={{ margin: '6px 0 12px', color: '#6c757d' }}>Join our community and stay in the loop.</p>
                            <div className="chapter-public__cta-row">
                                {ctaGroupMe && (
                                    <a href={ctaGroupMe} target="_blank" rel="noreferrer">
                                        <button>Join our Discord</button>
                                    </a>
                                )}
                                {ctaEmail && (
                                    <a href={ctaEmail} target="_blank" rel="noreferrer">
                                        <button>Join Our Email List</button>
                                    </a>
                                )}
                                {ctaInstagram && (
                                    <a href={ctaInstagram} target="_blank" rel="noreferrer">
                                        <button>Follow on Instagram</button>
                                    </a>
                                )}
                                {ctaWebsite && (
                                    <a href={ctaWebsite} target="_blank" rel="noreferrer">
                                        <button>Chapter Website</button>
                                    </a>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}
